"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MathJaxRenderer = void 0;
const chtml_1 = require("mathjax-full/js/output/chtml");
const assistive_mml_1 = require("mathjax-full/js/a11y/assistive-mml");
const html_1 = require("mathjax-full/js/handlers/html");
const browserAdaptor_1 = require("mathjax-full/js/adaptors/browserAdaptor");
const mathjax_1 = require("mathjax-full/js/mathjax");
const tex_1 = require("mathjax-full/js/input/tex");
const preprocessing_1 = require("./preprocessing");
const postprocessing_1 = require("./postprocessing");
const general_purpose_1 = require("./general-purpose");
const tex_input_config_1 = require("./tex-input-config");
const sre_1 = require("./sre");
// HEADS UP! You probably shouldn't import/use the MathJaxRenderer class
// directly. Instead, use the functions in render.ts to render math with
// MathJax.
class MathJaxRenderer {
    constructor({ fontURL, shouldFixUnicodeLayout, locale }) {
        this.renderedAnything = false;
        this.lastRenderingError = null;
        this.render = (tex) => {
            this.renderedAnything = true;
            try {
                const { normalizedTex, normalizedTexWithMathjaxWorkarounds } = this.preprocessor.preprocessTex(tex);
                const vanillaMathjaxDOM = this.getTexToChtmlConverter().convert(normalizedTexWithMathjaxWorkarounds, { display: false });
                const domElement = (0, postprocessing_1.postprocessDom)(vanillaMathjaxDOM);
                // MathJax doesn't log non-fatal errors like undefined macros,
                // because we have the noerrors package installed. However, we want
                // to get notified of such errors, so we detect them ourselves.
                // MathJax outputs the bad TeX in red, so we detect that here. This
                // method of detection might result in false positives, but that's
                // acceptable since we're just logging.
                let error = this.lastRenderingError;
                if (domElement.outerHTML.includes('<mtext mathcolor="red">')) {
                    error = new Error("MathJax rendering error");
                }
                const getReadableText = () => (0, sre_1.mmlToSpeechAsync)(domElement.getElementsByTagName("math")[0].outerHTML, this.locale);
                const addLabel = async () => {
                    const text = await getReadableText();
                    domElement.setAttribute("aria-label", text);
                };
                return {
                    domElement,
                    normalizedTex,
                    error,
                    getReadableText,
                    addLabel,
                };
            }
            catch (e) {
                const error = (0, general_purpose_1.convertCaughtValueToError)(e);
                const domElement = (0, general_purpose_1.createSpanWithRedText)(tex);
                // TODO(benchristel): it's unclear what we should return for
                // normalizedTex here. Since this is an error case, it probably
                // doesn't matter much.
                return {
                    domElement,
                    normalizedTex: tex,
                    getReadableText: () => Promise.resolve(""),
                    addLabel: () => Promise.resolve(),
                    error,
                };
            }
            finally {
                this.lastRenderingError = null;
            }
        };
        // Since MathJax isn't adding the math elements to the dom itself (we are,
        // via React), it doesn't know when to update its stylesheet. We can call
        // updateStyles() after rendering new math content to make sure that all
        // the elements will display correctly.
        this.updateStyles = () => {
            var _a;
            const styleSheet = this.getStyleSheet();
            if (document.getElementById(chtml_1.CHTML.STYLESHEETID) == null) {
                (_a = document.head) === null || _a === void 0 ? void 0 : _a.appendChild(styleSheet);
            }
        };
        this.getStyleSheet = () => {
            // @ts-expect-error - TS2322 - Type 'unknown' is not assignable to type 'HTMLStyleElement'.
            return this.getChtmlOutputJax().styleSheet(this.getTexToChtmlConverter());
        };
        this.hasRenderedAnything = () => {
            return this.renderedAnything;
        };
        this.resetGlobalState = () => {
            this.renderedAnything = false;
            this.getChtmlOutputJax().clearCache();
        };
        this.getChtmlOutputJax = (0, general_purpose_1.lazy)(() => new chtml_1.CHTML({ fontURL: this.fontURL }));
        this.getTexToChtmlConverter = (0, general_purpose_1.lazy)(() => {
            if (!windowGlobalExists()) {
                throw new Error("Can't render TeX without a DOM environment");
            }
            return mathjax_1.mathjax.document("", {
                InputJax: this.getTexInputJax(),
                OutputJax: this.getChtmlOutputJax(),
            });
        });
        this.getTexInputJax = (0, general_purpose_1.lazy)(() => {
            return new tex_1.TeX({
                ...tex_input_config_1.texInputConfig,
                formatError: (jax, err) => {
                    this.lastRenderingError = err;
                    // @ts-expect-error - TS2571 - Object is of type 'unknown'.
                    return jax.formatError(err);
                },
            });
        });
        this.fontURL = fontURL;
        this.preprocessor = new preprocessing_1.MathJaxPreprocessor({ shouldFixUnicodeLayout });
        this.locale = locale;
    }
}
exports.MathJaxRenderer = MathJaxRenderer;
if (windowGlobalExists()) {
    // Render both visible HTML and accessible MML.
    (0, assistive_mml_1.AssistiveMmlHandler)((0, html_1.RegisterHTMLHandler)((0, browserAdaptor_1.browserAdaptor)()));
}
function windowGlobalExists() {
    return typeof window !== "undefined";
}
